import { useState } from "react";
import { Portal } from "@mui/material";
import { Map } from "leaflet";

import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { v4 as uuidv4 } from "uuid";

import { useUpdateMeasureConfigs } from "hooks/measureConfigHooks";

import { useNavigate } from "hooks/routerHooks";

import {
  ConfigParameters,
  FormMeasureProperty,
  MeasureForm,
  MeasureImpact,
  MeasureConfLayer,
  MeasureConf,
  StudyArea,
  DrawState,
  DrawType,
} from "types/types";

import { RenderStudyArea } from "components/map/layers/RenderStudyArea";
import { AddMeasureFooter } from "components/footer/AddMeasureFooter";
import { RenderMeasureConfigs } from "components/measures/addMeasurePage/RenderMeasureConfigs";
import { MeasureImpactForm } from "components/form/MeasureImpactForm";
import { DrawMeasureSection } from "components/measures/addMeasurePage/DrawMeasureSection";
import { useInitialValues } from "hooks/measureHooks";

interface Props {
  mapRef: Map;
  footerRef: HTMLDivElement;
  uuid: string;
  studyArea: StudyArea;
  config: ConfigParameters;
  measureConfigId: number;
  backendMeasureConfigs: MeasureConf[];
  selectedMeasure: MeasureImpact;
  isHardenedSurface: boolean;
  isNewMeasureConfig: boolean;
}

export function AddMeasureConfigsDrawerContent(props: Props) {
  const {
    mapRef,
    footerRef,
    uuid,
    studyArea,
    config,
    measureConfigId,
    backendMeasureConfigs,
    selectedMeasure,
    isHardenedSurface,
    isNewMeasureConfig,
  } = props;

  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const { mutateAsync: update, isLoading } = useUpdateMeasureConfigs(uuid);
  const [measureConfigurations, setMeasureConfigurations] = useState<
    MeasureConf[]
  >(backendMeasureConfigs);

  const initialValues = useInitialValues(
    selectedMeasure.measure,
    measureConfigId,
    measureConfigurations
  );


  const diameterProperty = initialValues.measureProperties.find(
    (prop) => prop.propertyKey === "diameter"
  );

  const [radius, setRadius] = useState(
    diameterProperty ? diameterProperty.value / 2 : 1.5
  );

  function addMeasurePropetiesToMeasureConfig(
    measureConfigs: MeasureConf[],
    measureProperties: FormMeasureProperty[]
  ) {
    const updatedMeasureConfigs = measureConfigs.map((measureConfig) => {
      if (measureConfig.id !== measureConfigId) return measureConfig;
      return {
        ...measureConfig,
        propertyValueList: measureProperties,
      };
    });
    return updatedMeasureConfigs;
  }

  async function onSubmitForm(formData: MeasureForm) {
    const updatedMeasureConfigs = addMeasurePropetiesToMeasureConfig(
      measureConfigurations,
      formData.measureProperties
    );
    saveMeasureConfigs(updatedMeasureConfigs);
  }

  function onSaveMeasureConfigs() {
    saveMeasureConfigs(measureConfigurations);
  }

  async function saveMeasureConfigs(measureConfigs: MeasureConf[]) {
    const filteredMeasureConfigs = measureConfigs.filter(
      (measureConfig) => measureConfig.layers.length > 0
    );
    update(filteredMeasureConfigs, {
      onSuccess: () => {
        navigate(`/${uuid}/measures`);
        enqueueSnackbar(t("label.newMeasureAdded"), {
          variant: "success",
        });
      },
    });
  }

  function updateMeasureConfigurations(updatedMeasureConfigs: MeasureConf[]) {
    setMeasureConfigurations(updatedMeasureConfigs);
  }

  function removeLayerFromMeasureConfig(layerId: number) {
    const updatedMeasureConfigs = measureConfigurations.map((measureConfig) => {
      if (measureConfig.id === measureConfigId) {
        return {
          ...measureConfig,
          layers: measureConfig.layers.filter((layer) => layer.id !== layerId),
        };
      }
      return measureConfig;
    });
    setMeasureConfigurations(updatedMeasureConfigs);
  }

  function addNewGeometryToMeasureConfig(
    newLayer: MeasureConfLayer,
    updatedMeasureConfigs: MeasureConf[]
  ) {
    return addNewLayerToMeasureConfig(newLayer, updatedMeasureConfigs);
  }

  function addNewLayerToMeasureConfig(
    newLayer: MeasureConfLayer,
    updatedMeasureConfigs: MeasureConf[]
  ) {
    const updatedMeasureConfigsWithNewLayer = updatedMeasureConfigs.map(
      (measureConfig) => {
        if (measureConfig.id === measureConfigId) {
          return {
            ...measureConfig,
            layers: [...measureConfig.layers, newLayer],
          };
        }
        return measureConfig;
      }
    );
    return setMeasureConfigurations([...updatedMeasureConfigsWithNewLayer]);
  }

  function onDiameterChanged(diameter: number) {
    if (!diameterProperty) return;
    const radius = diameter / 2;
    const minDiameter = diameterProperty.minimum ? diameterProperty.minimum : 0;
    const maxDiameter = diameterProperty.maximum ? diameterProperty.maximum : 0;
    const minRadius = minDiameter > 0 ? minDiameter / 2 : 0;
    const maxRadius = maxDiameter > 0 ? maxDiameter / 2 : 0;

    if (radius < minRadius || radius > maxRadius) return;
    setRadius(radius);
  }

  const [drawState, setDrawState] = useState<DrawState>({
    drawType: "create",
    uuid: "",
  });

  function updateDrawState(drawState?: DrawType) {
    if (!drawState) {
      return setDrawState((prev) => {
        return {
          ...prev,
          uuid: uuidv4(),
        };
      });
    }
    return setDrawState({ drawType: drawState, uuid: uuidv4() });
  }

  return (
    <>
      {!isHardenedSurface &&
        selectedMeasure.measure.measurePropertyLinkList.length > 0 && (
          <MeasureImpactForm
            selectedMeasure={selectedMeasure.measure}
            measureConfigid={measureConfigId}
            measureConfigs={measureConfigurations}
            onSubmitForm={onSubmitForm}
            onDiameterChanged={onDiameterChanged}
          />
        )}

      <DrawMeasureSection
        mapRef={mapRef}
        drawState={drawState}
        selectedMeasure={selectedMeasure}
        measureConfigId={measureConfigId}
        measureConfigurations={measureConfigurations}
        basePolygon={studyArea.geometry_4326}
        minArea={config.measureAreaMin_m2}
        radius={radius}
        updateDrawState={updateDrawState}
        addNewGeometryToMeasureConfig={addNewGeometryToMeasureConfig}
        removeLayerFromMeasureConfig={removeLayerFromMeasureConfig}
        updateMeasureConfigurations={updateMeasureConfigurations}
      />

      <RenderMeasureConfigs
        mapRef={mapRef}
        radius={radius}
        measureConfigs={measureConfigurations}
        measureConfigId={measureConfigId}
      />
      <RenderStudyArea mapRef={mapRef} studyAreaGeo={studyArea.geometry_4326} />
      <Portal container={footerRef}>
        <AddMeasureFooter
          currentDrawState={drawState.drawType}
          selectedMeasure={selectedMeasure}
          measureConfigId={measureConfigId}
          measureConfigs={measureConfigurations}
          uuid={uuid}
          isLoading={isLoading}
          isNewMeasureConfig={isNewMeasureConfig}
          onSaveMeasureConfigs={onSaveMeasureConfigs}
        />
      </Portal>
    </>
  );
}
